import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useAuth } from "@/contexts/AuthContext"
import {
  User,
  Building2,
  Key,
  Camera,
  Users,
  Copy,
  Check,
  Loader2,
  X,
  UserMinus,
  Crown,
  Mail,
  Shield,
  ChevronRight,
  LogOut,
  Wallet,
  DollarSign,
} from "lucide-react"

interface Agent {
  _id: string
  email: string
  displayName: string
  joinedAt: string
  totalDeals: number
}

interface ProfileSettingsProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ProfileSettings({ open, onOpenChange }: ProfileSettingsProps) {
  const { user, token, refreshUser, getProfilePicturePresignedUrl, updateProfilePicture, logout } = useAuth()
  const [activeTab, setActiveTab] = React.useState<"profile" | "payment" | "password" | "agents">("profile")
  const [copied, setCopied] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [agents, setAgents] = React.useState<Agent[]>([])
  const [isLoadingAgents, setIsLoadingAgents] = React.useState(false)
  const [isUploadingPicture, setIsUploadingPicture] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Form states
  const [displayName, setDisplayName] = React.useState("")
  const [currentPassword, setCurrentPassword] = React.useState("")
  const [newPassword, setNewPassword] = React.useState("")
  const [confirmPassword, setConfirmPassword] = React.useState("")
  const [message, setMessage] = React.useState<{ type: "success" | "error"; text: string } | null>(null)

  // Payment form states
  const [paymentMethods, setPaymentMethods] = React.useState<string[]>([])
  const [cashappHandle, setCashappHandle] = React.useState("")
  const [venmoHandle, setVenmoHandle] = React.useState("")
  const [zelleEmail, setZelleEmail] = React.useState("")
  const [paypalEmail, setPaypalEmail] = React.useState("")
  const [isSavingPayment, setIsSavingPayment] = React.useState(false)

  const isAgency = user?.onboardingData?.userType === "agency"
  const agencyId = user?.generatedAgencyId || user?.onboardingData?.agencyId

  React.useEffect(() => {
    if (user) {
      setDisplayName(user.onboardingData?.displayName || user.onboardingData?.agencyName || "")
      // Load existing payment info
      const pi = user.onboardingData?.paymentInfo
      if (pi) {
        setPaymentMethods(pi.methods || [])
        setCashappHandle(pi.cashappHandle || "")
        setVenmoHandle(pi.venmoHandle || "")
        setZelleEmail(pi.zelleEmail || "")
        setPaypalEmail(pi.paypalEmail || "")
      }
    }
  }, [user])

  React.useEffect(() => {
    if (open && isAgency && activeTab === "agents") {
      fetchAgents()
    }
  }, [open, isAgency, activeTab])

  const fetchAgents = async () => {
    if (!token || !agencyId) return
    setIsLoadingAgents(true)
    try {
      const response = await fetch(`/api/agency/agents?agencyId=${encodeURIComponent(agencyId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json()
      if (data.success) {
        setAgents(data.agents || [])
      }
    } catch (error) {
      console.error("Failed to fetch agents:", error)
    } finally {
      setIsLoadingAgents(false)
    }
  }

  const copyAgencyId = () => {
    if (agencyId) {
      navigator.clipboard.writeText(agencyId)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setMessage(null)

    try {
      const response = await fetch("/api/profile/update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ displayName }),
      })
      const data = await response.json()
      if (data.success) {
        setMessage({ type: "success", text: "Profile updated successfully!" })
        await refreshUser()
      } else {
        setMessage({ type: "error", text: data.message || "Failed to update profile" })
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to update profile" })
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "Passwords do not match" })
      return
    }
    if (newPassword.length < 6) {
      setMessage({ type: "error", text: "Password must be at least 6 characters" })
      return
    }

    setIsLoading(true)
    setMessage(null)

    try {
      const response = await fetch("/api/profile/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      })
      const data = await response.json()
      if (data.success) {
        setMessage({ type: "success", text: "Password changed successfully!" })
        setCurrentPassword("")
        setNewPassword("")
        setConfirmPassword("")
      } else {
        setMessage({ type: "error", text: data.message || "Failed to change password" })
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to change password" })
    } finally {
      setIsLoading(false)
    }
  }

  const togglePaymentMethod = (method: string) => {
    setPaymentMethods((prev) =>
      prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method]
    )
  }

  const handleUpdatePaymentInfo = async (e: React.FormEvent) => {
    e.preventDefault()
    if (paymentMethods.length === 0) {
      setMessage({ type: "error", text: "Please select at least one payment method" })
      return
    }
    setIsSavingPayment(true)
    setMessage(null)

    try {
      const response = await fetch("/api/profile/update-payment-info", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          paymentInfo: {
            methods: paymentMethods,
            cashappHandle,
            venmoHandle,
            zelleEmail,
            paypalEmail,
          },
        }),
      })
      const data = await response.json()
      if (data.success) {
        setMessage({ type: "success", text: "Payment info updated!" })
        await refreshUser()
      } else {
        setMessage({ type: "error", text: data.message || "Failed to update payment info" })
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to update payment info" })
    } finally {
      setIsSavingPayment(false)
    }
  }

  const handleKickAgent = async (agentId: string) => {
    if (!confirm("Are you sure you want to remove this agent from your agency?")) return

    try {
      const response = await fetch("/api/agency/kick-agent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ agentId }),
      })
      const data = await response.json()
      if (data.success) {
        setAgents(agents.filter(a => a._id !== agentId))
        setMessage({ type: "success", text: "Agent removed from agency" })
      } else {
        setMessage({ type: "error", text: data.message || "Failed to remove agent" })
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to remove agent" })
    }
  }

  const handleProfilePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"]
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: "error", text: "Only JPEG, PNG, WebP, and GIF images are allowed" })
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: "error", text: "Image must be less than 5MB" })
      return
    }

    setIsUploadingPicture(true)
    setMessage(null)

    try {
      // Get presigned URL
      const presignedResult = await getProfilePicturePresignedUrl(file.name, file.type)
      if (!presignedResult.success || !presignedResult.uploadUrl || !presignedResult.fileUrl) {
        setMessage({ type: "error", text: presignedResult.message || "Failed to get upload URL" })
        return
      }

      // Upload to R2
      const uploadResponse = await fetch(presignedResult.uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      })

      if (!uploadResponse.ok) {
        setMessage({ type: "error", text: "Failed to upload image" })
        return
      }

      // Save the URL to the user's profile
      const updateResult = await updateProfilePicture(presignedResult.fileUrl)
      if (updateResult.success) {
        setMessage({ type: "success", text: "Profile picture updated!" })
        await refreshUser()
      } else {
        setMessage({ type: "error", text: updateResult.message || "Failed to save profile picture" })
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to upload profile picture" })
    } finally {
      setIsUploadingPicture(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  const tabs = [
    { id: "profile" as const, label: "Profile", icon: User },
    { id: "payment" as const, label: "Payment", icon: Wallet },
    { id: "password" as const, label: "Security", icon: Key },
    ...(isAgency ? [{ id: "agents" as const, label: "Team", icon: Users }] : []),
  ]

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg p-0 bg-gradient-to-br from-[#161628] to-[#d489a8] border-l-4 border-[#2A2A45] overflow-y-auto">
        <div className="p-6">
          <SheetHeader className="mb-6">
            <SheetTitle className="text-2xl font-black text-white flex items-center gap-3">
              <div className="p-2 bg-[#1C1C32] rounded-xl border-3 border-[#2A2A45] shadow-[0_0_8px_rgba(243,112,32,0.12)]">
                <User className="w-6 h-6 text-[#F37020]" />
              </div>
              Settings
            </SheetTitle>
          </SheetHeader>

          {/* Profile Card */}
          <motion.div
            className="bg-[#1C1C32] rounded-2xl border-3 border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.15)] p-4 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4">
              <div className="relative group">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleProfilePictureUpload}
                />
                <motion.button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPicture}
                  className="relative w-16 h-16 rounded-xl overflow-hidden border-3 border-[#2A2A45] shadow-[0_0_8px_rgba(243,112,32,0.12)] cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {user?.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#F37020] to-[#FF6B00] flex items-center justify-center">
                      <span className="text-white font-black text-2xl">
                        {displayName.charAt(0).toUpperCase() || "?"}
                      </span>
                    </div>
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    {isUploadingPicture ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5 text-white" />
                    )}
                  </div>
                </motion.button>
                {isAgency && (
                  <div className="absolute -top-1 -right-1 p-1 bg-yellow-400 rounded-full border-2 border-[#2A2A45] z-10">
                    <Crown className="w-3 h-3 text-black" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-black text-lg text-white truncate">{displayName || "User"}</h3>
                <div className="flex items-center gap-1 text-sm text-[#6A6A8A]">
                  <Mail className="w-3 h-3" />
                  <span className="truncate">{user?.email}</span>
                </div>
                {isAgency && (
                  <div className="flex items-center gap-1 mt-1">
                    <Shield className="w-3 h-3 text-purple-500" />
                    <span className="text-xs font-bold text-purple-600">Agency Owner</span>
                  </div>
                )}
              </div>
            </div>

            {/* Agency ID */}
            {agencyId && (
              <motion.button
                onClick={copyAgencyId}
                className="w-full mt-4 flex items-center justify-between gap-2 px-3 py-2 bg-[#1A1A30] border-2 border-[#2A2A45]/40 rounded-xl hover:bg-[#1A1A30] transition-colors"
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <span className="text-xs font-mono font-bold text-[#9A9ABF] truncate">{agencyId}</span>
                </div>
                {copied ? (
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : (
                  <Copy className="w-4 h-4 text-[#6A6A8A] flex-shrink-0" />
                )}
              </motion.button>
            )}
          </motion.div>

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id)
                  setMessage(null)
                }}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-bold text-sm
                  border-2 border-[#2A2A45] transition-all
                  ${activeTab === tab.id
                    ? "bg-[#F37020] text-white shadow-[0_0_8px_rgba(243,112,32,0.12)]"
                    : "bg-[#1C1C32] text-[#CCCCCC] hover:bg-[#1A1A30]"
                  }
                `}
                whileTap={{ scale: 0.95 }}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </motion.button>
            ))}
          </div>

          {/* Message Display */}
          <AnimatePresence>
            {message && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`mb-4 p-3 rounded-xl border-2 ${
                  message.type === "success"
                    ? "bg-green-50 border-green-300 text-green-700"
                    : "bg-red-50 border-red-300 text-red-700"
                }`}
              >
                <p className="text-sm font-medium">{message.text}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === "profile" && (
              <motion.form
                key="profile"
                onSubmit={handleUpdateProfile}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="bg-[#1C1C32] rounded-2xl border-3 border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.15)] p-4">
                  <label className="block text-sm font-bold text-[#CCCCCC] mb-2">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full px-4 py-3 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#F37020]"
                    placeholder="Your display name"
                  />
                </div>

                <div className="bg-[#1C1C32] rounded-2xl border-3 border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.15)] p-4">
                  <label className="block text-sm font-bold text-[#CCCCCC] mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={user?.email || ""}
                    disabled
                    className="w-full px-4 py-3 bg-[#1A1A30] border-2 border-[#2A2A45]/60 rounded-xl font-medium text-[#6A6A8A]"
                  />
                  <p className="text-xs text-[#6A6A8A] mt-1">Email cannot be changed</p>
                </div>

                <motion.button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-[#F37020] text-white font-bold rounded-xl border-3 border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.15)] disabled:opacity-50"
                  whileHover={{ scale: 1.02, boxShadow: "6px 6px 0 0 #1a1a1a" }}
                  whileTap={{ scale: 0.98, boxShadow: "2px 2px 0 0 #1a1a1a" }}
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    "Save Changes"
                  )}
                </motion.button>

                {/* Sign Out Button */}
                <motion.button
                  type="button"
                  onClick={logout}
                  className="w-full py-3 px-4 mt-4 bg-red-600/20 text-red-500 font-bold rounded-xl border-2 border-red-500/30 hover:bg-red-600/30 hover:border-red-500/50 transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <LogOut className="w-4 h-4 inline-block mr-2" />
                  Sign Out
                </motion.button>
              </motion.form>
            )}

            {activeTab === "payment" && (
              <motion.form
                key="payment"
                onSubmit={handleUpdatePaymentInfo}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                {/* Payment Methods Selection */}
                <div className="bg-[#1C1C32] rounded-2xl border-3 border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.15)] p-4">
                  <label className="block text-sm font-bold text-[#CCCCCC] mb-3">
                    Payment Methods
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: "cashapp", label: "CashApp", color: "#00D632" },
                      { id: "venmo", label: "Venmo", color: "#3D95CE" },
                      { id: "zelle", label: "Zelle", color: "#6C1CD3" },
                      { id: "paypal", label: "PayPal", color: "#003087" },
                    ].map((method) => (
                      <motion.button
                        key={method.id}
                        type="button"
                        onClick={() => togglePaymentMethod(method.id)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 font-bold text-sm transition-all ${
                          paymentMethods.includes(method.id)
                            ? "border-[#F37020] bg-[#F37020]/10 text-white"
                            : "border-[#2A2A45] bg-[#1A1A30] text-[#6A6A8A] hover:border-[#3A3A55]"
                        }`}
                        whileTap={{ scale: 0.95 }}
                      >
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: method.color }}
                        />
                        {method.label}
                        {paymentMethods.includes(method.id) && (
                          <Check className="w-3.5 h-3.5 text-[#F37020] ml-auto" />
                        )}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Payment Details */}
                <div className="bg-[#1C1C32] rounded-2xl border-3 border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.15)] p-4 space-y-4">
                  {paymentMethods.includes("cashapp") && (
                    <div>
                      <label className="block text-sm font-bold text-[#CCCCCC] mb-2">
                        <span className="inline-flex items-center gap-1.5">
                          <DollarSign className="w-3.5 h-3.5 text-[#00D632]" />
                          CashApp Handle
                        </span>
                      </label>
                      <input
                        type="text"
                        value={cashappHandle}
                        onChange={(e) => setCashappHandle(e.target.value)}
                        className="w-full px-4 py-3 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#00D632]"
                        placeholder="$YourCashtag"
                      />
                    </div>
                  )}

                  {paymentMethods.includes("venmo") && (
                    <div>
                      <label className="block text-sm font-bold text-[#CCCCCC] mb-2">
                        <span className="inline-flex items-center gap-1.5">
                          <DollarSign className="w-3.5 h-3.5 text-[#3D95CE]" />
                          Venmo Handle
                        </span>
                      </label>
                      <input
                        type="text"
                        value={venmoHandle}
                        onChange={(e) => setVenmoHandle(e.target.value)}
                        className="w-full px-4 py-3 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#3D95CE]"
                        placeholder="@YourVenmo"
                      />
                    </div>
                  )}

                  {paymentMethods.includes("zelle") && (
                    <div>
                      <label className="block text-sm font-bold text-[#CCCCCC] mb-2">
                        <span className="inline-flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-[#6C1CD3]" />
                          Zelle Email
                        </span>
                      </label>
                      <input
                        type="email"
                        value={zelleEmail}
                        onChange={(e) => setZelleEmail(e.target.value)}
                        className="w-full px-4 py-3 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#6C1CD3]"
                        placeholder="your@email.com"
                      />
                    </div>
                  )}

                  {paymentMethods.includes("paypal") && (
                    <div>
                      <label className="block text-sm font-bold text-[#CCCCCC] mb-2">
                        <span className="inline-flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-[#003087]" />
                          PayPal Email
                        </span>
                      </label>
                      <input
                        type="email"
                        value={paypalEmail}
                        onChange={(e) => setPaypalEmail(e.target.value)}
                        className="w-full px-4 py-3 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#003087]"
                        placeholder="your@email.com"
                      />
                    </div>
                  )}

                  {paymentMethods.length === 0 && (
                    <p className="text-sm text-[#6A6A8A] text-center py-4">
                      Select a payment method above to enter your details
                    </p>
                  )}
                </div>

                <motion.button
                  type="submit"
                  disabled={isSavingPayment || paymentMethods.length === 0}
                  className="w-full py-3 px-4 bg-[#F37020] text-white font-bold rounded-xl border-3 border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.15)] disabled:opacity-50"
                  whileHover={{ scale: 1.02, boxShadow: "6px 6px 0 0 #1a1a1a" }}
                  whileTap={{ scale: 0.98, boxShadow: "2px 2px 0 0 #1a1a1a" }}
                >
                  {isSavingPayment ? (
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    "Save Payment Info"
                  )}
                </motion.button>
              </motion.form>
            )}

            {activeTab === "password" && (
              <motion.form
                key="password"
                onSubmit={handleChangePassword}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="bg-[#1C1C32] rounded-2xl border-3 border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.15)] p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-[#CCCCCC] mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#F37020]"
                      placeholder="••••••••"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-[#CCCCCC] mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#F37020]"
                      placeholder="••••••••"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-[#CCCCCC] mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#F37020]"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={isLoading || !currentPassword || !newPassword || !confirmPassword}
                  className="w-full py-3 px-4 bg-[#F37020] text-white font-bold rounded-xl border-3 border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.15)] disabled:opacity-50"
                  whileHover={{ scale: 1.02, boxShadow: "6px 6px 0 0 #1a1a1a" }}
                  whileTap={{ scale: 0.98, boxShadow: "2px 2px 0 0 #1a1a1a" }}
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    "Change Password"
                  )}
                </motion.button>
              </motion.form>
            )}

            {activeTab === "agents" && isAgency && (
              <motion.div
                key="agents"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="bg-[#1C1C32] rounded-2xl border-3 border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.15)] p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-white">Your Team</h3>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">
                      {agents.length} {agents.length === 1 ? "agent" : "agents"}
                    </span>
                  </div>

                  {isLoadingAgents ? (
                    <div className="py-8 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-[#6A6A8A]" />
                    </div>
                  ) : agents.length === 0 ? (
                    <div className="py-8 text-center">
                      <Users className="w-12 h-12 text-[#6A6A8A] mx-auto mb-3" />
                      <p className="text-[#6A6A8A] font-medium">No agents yet</p>
                      <p className="text-xs text-[#6A6A8A] mt-1">
                        Share your agency ID to invite agents
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {agents.map((agent) => (
                        <motion.div
                          key={agent._id}
                          className="flex items-center gap-3 p-3 bg-[#1A1A30] rounded-xl border-2 border-[#2A2A45]/40"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center border-2 border-[#2A2A45]">
                            <span className="text-white font-bold">
                              {agent.displayName?.charAt(0).toUpperCase() || "?"}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-white truncate">
                              {agent.displayName || "Agent"}
                            </p>
                            <p className="text-xs text-[#6A6A8A] truncate">{agent.email}</p>
                          </div>
                          <div className="text-right mr-2">
                            <p className="text-sm font-bold text-[#F37020]">{agent.totalDeals}</p>
                            <p className="text-[10px] text-[#6A6A8A]">deals</p>
                          </div>
                          <motion.button
                            onClick={() => handleKickAgent(agent._id)}
                            className="p-2 bg-red-50 hover:bg-red-100 rounded-lg border-2 border-red-200 transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title="Remove agent"
                          >
                            <UserMinus className="w-4 h-4 text-red-500" />
                          </motion.button>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </SheetContent>
    </Sheet>
  )
}
